import React, { forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Input Component
 * 
 * Text input s label, placeholder, error state
 */
export const Input = forwardRef(({
  label,
  helper,
  error,
  required,
  fullWidth = false,
  className = '',
  ...props
}, ref) => {
  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 500,
            color: '#333333',
            marginBottom: '6px',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#E53935', marginLeft: '4px' }}>*</span>
          )}
        </label>
      )}

      <input
        ref={ref}
        className={`input ${error ? 'input-error' : ''} ${className}`}
        style={{
          width: '100%',
          height: '44px',
          padding: '12px 16px',
          border: `1px solid ${error ? '#F44336' : '#E0E0E0'}`,
          borderRadius: '8px',
          backgroundColor: error ? '#FFEBEE' : '#FFFFFF',
          fontSize: '14px',
          color: '#333333',
          outline: 'none',
          transition: 'all 150ms ease',
          fontFamily: 'inherit',
        }}
        onFocus={(e) => {
          if (!error) {
            e.currentTarget.style.borderColor = '#E53935';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(229, 57, 53, 0.1)';
          }
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? '#F44336' : '#E0E0E0';
          e.currentTarget.style.boxShadow = 'none';
        }}
        {...props}
      />

      {error && (
        <p style={{ fontSize: '12px', color: '#F44336', margin: '4px 0 0' }}>
          {error}
        </p>
      )}

      {helper && !error && (
        <p style={{ fontSize: '12px', color: '#666666', margin: '4px 0 0' }}>
          {helper}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

/**
 * Password Input - Input s toggle pro zobrazení hesla
 */
export const PasswordInput = forwardRef(({
  label,
  helper,
  error,
  required,
  fullWidth = false,
  className = '',
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 500,
            color: '#333333',
            marginBottom: '6px',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#E53935', marginLeft: '4px' }}>*</span>
          )}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        <input
          ref={ref}
          type={showPassword ? 'text' : 'password'}
          className={`input ${error ? 'input-error' : ''} ${className}`}
          style={{
            width: '100%',
            height: '44px',
            padding: '12px 44px 12px 16px',
            border: `1px solid ${error ? '#F44336' : '#E0E0E0'}`,
            borderRadius: '8px',
            backgroundColor: error ? '#FFEBEE' : '#FFFFFF',
            fontSize: '14px',
            color: '#333333',
            outline: 'none',
            transition: 'all 150ms ease',
            fontFamily: 'inherit',
          }}
          onFocus={(e) => {
            if (!error) {
              e.currentTarget.style.borderColor = '#E53935';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(229, 57, 53, 0.1)';
            }
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = error ? '#F44336' : '#E0E0E0';
            e.currentTarget.style.boxShadow = 'none';
          }}
          {...props}
        />

        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999999',
            transition: 'all 150ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F5F5F5';
            e.currentTarget.style.color = '#666666';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#999999';
          }}
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {error && (
        <p style={{ fontSize: '12px', color: '#F44336', margin: '4px 0 0' }}>
          {error}
        </p>
      )}

      {helper && !error && (
        <p style={{ fontSize: '12px', color: '#666666', margin: '4px 0 0' }}>
          {helper}
        </p>
      )}
    </div>
  );
});

PasswordInput.displayName = 'PasswordInput';

/**
 * TextArea Component
 */
export const TextArea = forwardRef(({
  label,
  helper,
  error,
  required,
  fullWidth = false,
  rows = 4,
  className = '',
  ...props
}, ref) => {
  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 500,
            color: '#333333',
            marginBottom: '6px',
          }}
        >
          {label}
          {required && (
            <span style={{ color: '#E53935', marginLeft: '4px' }}>*</span>
          )}
        </label>
      )}

      <textarea
        ref={ref}
        rows={rows}
        className={`textarea ${error ? 'textarea-error' : ''} ${className}`}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: `1px solid ${error ? '#F44336' : '#E0E0E0'}`,
          borderRadius: '8px',
          backgroundColor: error ? '#FFEBEE' : '#FFFFFF',
          fontSize: '14px',
          color: '#333333',
          outline: 'none',
          transition: 'all 150ms ease',
          fontFamily: 'inherit',
          resize: 'vertical',
          minHeight: '100px',
        }}
        onFocus={(e) => {
          if (!error) {
            e.currentTarget.style.borderColor = '#E53935';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(229, 57, 53, 0.1)';
          }
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? '#F44336' : '#E0E0E0';
          e.currentTarget.style.boxShadow = 'none';
        }}
        {...props}
      />

      {error && (
        <p style={{ fontSize: '12px', color: '#F44336', margin: '4px 0 0' }}>
          {error}
        </p>
      )}

      {helper && !error && (
        <p style={{ fontSize: '12px', color: '#666666', margin: '4px 0 0' }}>
          {helper}
        </p>
      )}
    </div>
  );
});

TextArea.displayName = 'TextArea';

export default Input;
